import React, { useState, useMemo, useCallback } from "react";
import { Row, Col } from "antd";
import { useNavigate } from "react-router-dom";
import { InfoCircleOutlined } from "@ant-design/icons";
import TicketStatusModal from "./TicketStatusModal";
import dayjs from "dayjs";
import { useAppSelector } from "../../../hooks/reduxHooks";

export default function MainCards({ ctaCount, creditBalance, messageCount }) {
  const navigate = useNavigate();
  const [ticketModalVisible, setTicketModalVisible] = useState(false);
  const { dateRangeValue } = useAppSelector((state) => state.dateRange);

  const startDateStr = useMemo(() =>
    dayjs(dateRangeValue.start_date).hour(0).minute(0).second(0).format("YYYY-MM-DD HH:mm:ss"),
    [dateRangeValue.start_date]
  );
  const endDateStr = useMemo(() =>
    dayjs(dateRangeValue.end_date).hour(23).minute(59).second(59).format("YYYY-MM-DD HH:mm:ss"),
    [dateRangeValue.end_date]
  );

  // Memoize processed data to prevent recalculation on every render
  const processedData = useMemo(() => {
    // Handle null/falsy values with safe fallbacks
    const ctaData = ctaCount?.data ?? [];
    const safeCtaCount = ctaCount || {};
    const safeCreditBalance = creditBalance || {};
    const safeMessageCount = messageCount || [];

    // Check if we have order type data
    const hasOrders = ctaData.some(item => item?.type === "order");
    const hasBookings = ctaData.some(item => item?.type === "booking");
    
    // Get order/booking data - prefer orders if available, otherwise bookings
    const orderBookingType = hasOrders ? "order" : hasBookings ? "booking" : null;

    // Get pending orders/bookings (status = 'open')
    const pendingOrdersBookings = ctaData
      .filter(item => item?.type === orderBookingType && item?.status === "open")
      .reduce((sum, item) => sum + (item?.count || 0), 0);
    
    // Get resolved orders/bookings (status = 'resolved')
    const resolvedOrdersBookings = ctaData
      .filter(item => item?.type === orderBookingType && item?.status === "resolved")
      .reduce((sum, item) => sum + (item?.count || 0), 0);

    // Calculate total pending tickets
    const totalPendingTickets = ctaData
      .filter(item => 
        item && 
        item.type && 
        item.type !== "order" && 
        item.type !== "booking" && 
        item.status === "open"
      )
      .reduce((sum, item) => sum + (item?.count || 0), 0);

    // Calculate total resolved tickets
    const totalResolvedTickets = ctaData
      .filter(item => 
        item && 
        item.type && 
        item.type !== "order" && 
        item.type !== "booking" && 
        item.status === "resolved"
      )
      .reduce((sum, item) => sum + (item?.count || 0), 0);

    // Get all unique ticket types (excluding order/booking)
    const uniqueTicketTypes = [...new Set(
      ctaData
        .filter(item => 
          item && 
          item.type && 
          item.type !== "order" && 
          item.type !== "booking"
        )
        .map(item => item.type)
    )];

    const hasMultipleTicketTypes = uniqueTicketTypes.length > 1;

    const totalCta = ctaData.reduce((sum, item) => {
      const count = item?.count || 0;
      return sum + count;
    }, 0);

    const totalCustomers = Array.isArray(safeMessageCount) 
      ? safeMessageCount.reduce((sum, item) => sum + (item?.unique_user_count || 0), 0) 
      : 0;

    return {
      ctaData,
      safeCtaCount,
      safeCreditBalance,
      hasOrders,
      orderBookingType,
      pendingOrdersBookings,
      resolvedOrdersBookings,
      totalPendingTickets,
      totalResolvedTickets,
      hasMultipleTicketTypes,
      totalCta,
      totalCustomers
    };
  }, [ctaCount, creditBalance, messageCount]);

  // Memoize navigation handlers to prevent function recreation
  const handleOrderBookingClick = useCallback(() => {
    const typeToUse = processedData.orderBookingType || "booking";
    const params = new URLSearchParams({ type: typeToUse, status: "open", start_date: startDateStr, end_date: endDateStr });
    navigate(`/cta?${params.toString()}`);
  }, [navigate, processedData.orderBookingType, startDateStr, endDateStr]);

  const handleOrderBookingTrendClick = useCallback(() => {
    const typeToUse = processedData.orderBookingType || "booking";
    const params = new URLSearchParams({ type: typeToUse, status: "resolved", start_date: startDateStr, end_date: endDateStr });
    navigate(`/cta?${params.toString()}`);
  }, [navigate, processedData.orderBookingType, startDateStr, endDateStr]);

  const handleTicketClick = useCallback(() => {
    if (processedData.hasMultipleTicketTypes) {
      setTicketModalVisible(true);
    } else {
      const params = new URLSearchParams({ type: "ticket", status: "open", start_date: startDateStr, end_date: endDateStr });
      navigate(`/cta?${params.toString()}`);
    }
  }, [navigate, processedData.hasMultipleTicketTypes, startDateStr, endDateStr]);

  const handleTicketTrendClick = useCallback(() => {
    if (processedData.hasMultipleTicketTypes) {
      setTicketModalVisible(true);
    } else {
      const params = new URLSearchParams({ type: "ticket", status: "resolved", start_date: startDateStr, end_date: endDateStr });
      navigate(`/cta?${params.toString()}`);
    }
  }, [navigate, processedData.hasMultipleTicketTypes, startDateStr, endDateStr]);

  const handleChannelsClick = useCallback(() => {
    navigate("/channels");
  }, [navigate]);

  const handleCtaClick = useCallback(() => {
    const params = new URLSearchParams({ start_date: startDateStr, end_date: endDateStr, status: "" });
    navigate(`/cta?${params.toString()}`);
  }, [navigate, startDateStr, endDateStr]);

  const handleSettingsClick = useCallback(() => {
    if (localStorage.getItem("role") === "admin") {
      navigate("/settings?tab=billing-usage");
    }
  }, [navigate]);

  const handleTicketModalToggle = useCallback(() => {
    setTicketModalVisible(prev => !prev);
  }, []);

  const handleTicketModalClose = useCallback(() => {
    setTicketModalVisible(false);
  }, []);

  // Memoize card data to prevent recreation on every render
  const cardData = useMemo(() => [
    {
      title: processedData.hasOrders ? "Pending Orders" : "Pending Bookings",
      value: processedData.pendingOrdersBookings,
      icon: processedData.hasOrders ? "lets-icons:order" : "icon-park-solid:appointment",
      trend: processedData.resolvedOrdersBookings + " resolved",
      trendIcon: "uil:comment-verify",
      subTrend: null,
      subTrendIcon: null,
      bg: "linear-gradient(135deg, #e6f2ff, #b3d7ff)",
      color: "#0052cc",
      accent: "#deebff",
      onClick: handleOrderBookingClick,
      trendOnClick: handleOrderBookingTrendClick,
    },
    {
      title: "Pending Tickets",
      value: processedData.totalPendingTickets,
      icon: "ic:baseline-contact-support",
      trend: processedData.totalResolvedTickets + " resolved",
      trendIcon: "uil:comment-verify",
      subTrend: null,
      subTrendIcon: null,
      bg: "linear-gradient(135deg, #e0f0ff, #b8dcff)",
      color: "#0065d2",
      accent: "#d4e9ff",
      showDetailsButton: processedData.hasMultipleTicketTypes,
      onClick: handleTicketClick,
      trendOnClick: handleTicketTrendClick,
    },
    {
      title: "Total Messages",
      value: processedData.safeCtaCount.total_chats ?? 0,
      icon: "ri:chat-smile-ai-fill",
      trend: processedData.totalCta + " Interventions",
      trendIcon: "fluent:record-stop-16-regular",
      subTrend: processedData.totalCustomers + " Customers",
      subTrendIcon: "flowbite:users-group-solid",
      bg: "linear-gradient(135deg, #e1eeff, #c2d9ff)",
      color: "#0747a6",
      accent: "#cce0ff",
      onClick: handleChannelsClick,
      trendOnClick: handleCtaClick,
      onSubTrendClick: handleChannelsClick,
    },
    {
      title: "Remaining Credits",
      value: (processedData.safeCreditBalance.remaining ?? 0).toFixed(2),
      icon: "bxs:coin-stack",
      trend: `${
        ((processedData.safeCreditBalance.total_credits || 0) - (processedData.safeCreditBalance.remaining || 0)).toFixed(2)
      } Used`,
      trendIcon: "mingcute:check-2-fill",
      subTrend: null,
      subTrendIcon: null,
      bg: "linear-gradient(135deg,rgb(197, 210, 226),rgb(188, 214, 255))",
      color: "#0747a6",
      accent: "#cce0ff",
      onClick: handleSettingsClick,
      trendOnClick: handleSettingsClick,
    },
  ], [processedData, handleOrderBookingClick, handleOrderBookingTrendClick, handleTicketClick, handleTicketTrendClick, handleChannelsClick, handleCtaClick, handleSettingsClick]);

  // Memoize TrendPill component to prevent recreation
  const TrendPill = useCallback(({ text, icon, onClick, accent }) => (
    <span
      onClick={onClick}
      onMouseEnter={(e) => (e.currentTarget.style.background = "#f0f0f0")}
      onMouseLeave={(e) => (e.currentTarget.style.background = accent)}
      style={{
        display: "inline-flex",
        alignItems: "center",
        fontSize: "12px",
        fontWeight: 500,
        padding: "4px 8px",
        borderRadius: "12px",
        background: accent,
        transition: "background-color 0.3s ease",
        cursor: "pointer",
      }}
    >
      <iconify-icon icon={icon} width="14" height="14" />
      <span style={{ marginLeft: 4 }}>{text}</span>
    </span>
  ), []);

  return (
    <>
      <Row gutter={[24, 24]}>
        {cardData.map((card, index) => (
          <Col key={index} xs={24} sm={12} lg={6}>
            <div
              onClick={card.onClick}
              style={{
                background: card.bg,
                borderRadius: "16px",
                boxShadow: "0 4px 12px rgba(0,0,0,0.05)",
                color: card.color,
                height: "190px",
                display: "flex",
                flexDirection: "column",
                justifyContent: "space-between",
                padding: "24px",
                position: "relative",
                overflow: "hidden",
                transition: "transform 0.3s ease, box-shadow 0.3s ease",
                cursor:
                  card?.title === "Remaining Credits" &&
                  localStorage.getItem("role") !== "admin"
                    ? "default"
                    : "pointer",
              }}
            >
              <div
                style={{
                  position: "absolute",
                  top: "-50px",
                  right: "-50px",
                  width: "120px",
                  height: "120px",
                  borderRadius: "50%",
                  background: card.accent,
                  opacity: 0.6,
                }}
              />
              
              {/* Details button for tickets card */}
              {card.showDetailsButton && (
                <div
                  onClick={(e) => {
                    e.stopPropagation();
                    handleTicketModalToggle();
                  }}
                  style={{
                    position: "absolute",
                    bottom: "16px",
                    right: "16px",
                    zIndex: 2,
                    width: "32px",
                    height: "32px",
                    borderRadius: "8px",
                    background: "rgba(255, 255, 255, 0.3)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    cursor: "pointer",
                    transition: "all 0.2s ease",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = "rgba(255, 255, 255, 0.5)";
                    e.currentTarget.style.transform = "scale(1.05)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = "rgba(255, 255, 255, 0.3)";
                    e.currentTarget.style.transform = "scale(1)";
                  }}
                >
                  <InfoCircleOutlined style={{ color: card.color, fontSize: "16px" }} />
                </div>
              )}

              <div style={{ display: "flex", justifyContent: "space-between", zIndex: 1 }}>
                <div
                  style={{
                    width: "48px",
                    height: "48px",
                    borderRadius: "12px",
                    background: card.accent,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <iconify-icon icon={card.icon} width="24" height="24" style={{ color: card.color }} />
                </div>
                <div style={{ textAlign: "right" }}>
                  {card.trend && (
                    <TrendPill
                      text={card.trend}
                      icon={card.trendIcon}
                      onClick={(e) => {
                        e.stopPropagation();
                        card.trendOnClick?.();
                      }}
                      accent={card.accent}
                    />
                  )}
                  <br />
                  {card.subTrend && card.subTrendIcon && (
                    <TrendPill
                      text={card.subTrend}
                      icon={card.subTrendIcon}
                      onClick={(e) => {
                        e.stopPropagation();
                        if (card.onSubTrendClick) {
                          card.onSubTrendClick();
                        }
                      }}
                      accent={card.accent}
                    />
                  )}
                </div>
              </div>
              <div style={{ zIndex: 1 }}>
                <div
                  style={{
                    fontSize: "14px",
                    fontWeight: 500,
                    marginBottom: "8px",
                    opacity: 0.9,
                  }}
                >
                  {card.title}
                </div>
                <div
                  style={{
                    fontSize: "34px",
                    fontWeight: 700,
                    lineHeight: 1,
                  }}
                >
                  {card.value}
                </div>
              </div>
            </div>
          </Col>
        ))}
      </Row>

      {/* Ticket Status Modal */}
      <TicketStatusModal 
        visible={ticketModalVisible}
        onClose={handleTicketModalClose}
        ctaData={processedData.ctaData}
      />
    </>
  );
}